import { Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  filter,
  map,
  Observable,
  ReplaySubject,
  Subject,
  switchMap,
} from 'rxjs';
import { Campaign, RecipientCampaignsResponse, SimpleCampaign } from '../marketing/campaign';
import { CampaignService as LegacyCampaignsService } from '../marketing/campaign.service';
import { CampaignsService } from '../marketing/marketing-automation-page/campaigns.service';

export interface RecipientCampaignsRequest {
  partnerId: string;
  accountGroupId: string;
  userEmail: string;
}

@Injectable({ providedIn: 'root' })
export class CampaignDialogStoreService {
  private pageLimit = 10;
  private status = ['published', 'active'];

  private campaigns$$: BehaviorSubject<SimpleCampaign[]> = new BehaviorSubject([]);
  private searchTerm$$: BehaviorSubject<string> = new BehaviorSubject(null);
  private loadingCampaigns$$: BehaviorSubject<boolean> = new BehaviorSubject(true);
  private offset$$: BehaviorSubject<number> = new BehaviorSubject(0);

  private recipientCampaigns$$: ReplaySubject<RecipientCampaignsResponse> = new ReplaySubject(1);
  private recipientCampaignsRequest$$: Subject<RecipientCampaignsRequest> = new Subject();
  private loadingRecipientCampiagns$$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  campaigns$: Observable<Campaign[]>;
  loadingCampaigns$: Observable<boolean> = this.loadingCampaigns$$.asObservable();
  recipientCampaigns$: Observable<RecipientCampaignsResponse> = this.recipientCampaigns$$.asObservable();

  constructor(
    private campaignsService: CampaignsService,
    private legacyCampaignsService: LegacyCampaignsService,
  ) {
    this.recipientCampaignsRequest$$
      .pipe(
        switchMap((req) =>
          this.legacyCampaignsService.getRecipientCampaigns(req.partnerId, req.accountGroupId, req.userEmail),
        ),
        map((response) => this.handleRecipientCampaigns(response)),
      )
      .subscribe();

    this.searchTerm$$
      .pipe(
        debounceTime(400),
        switchMap((searchTerm) => this.legacyCampaignsService.lookupCampaigns(searchTerm, this.status, this.pageLimit)),
        map((response) => this.handleCampaignResponse(response.campaigns)),
      )
      .subscribe();

    this.campaigns$ = combineLatest([this.campaigns$$, this.recipientCampaigns$$]).pipe(
      filter(([campaigns, recipientCampaigns]) => !!campaigns && !!recipientCampaigns),
      map(([campaigns, recipientCampaigns]) => {
        return campaigns.map((c) => {
          const cc = new Campaign();
          Object.assign(cc, c);
          const rc = recipientCampaigns[c.id];
          if (rc) {
            cc.recipientCampaignId = rc.recipientCampaignId;
            cc.recipientCampaignStatus = rc.status;
            cc.nextStepScheduledDate = rc.nextStepScheduledDate ? new Date(rc.nextStepScheduledDate) : null;
          }
          return cc;
        });
      }),
    );
  }

  init(partnerId: string, accountGroupId: string, userEmail: string): void {
    this.campaigns$$.next([]);
    this.offset$$.next(0);
    this.recipientCampaigns$$.next(null);
    this.loadingRecipientCampiagns$$.next(true);
    this.recipientCampaignsRequest$$.next({
      partnerId: partnerId,
      accountGroupId: accountGroupId,
      userEmail: userEmail,
    });
  }

  updateSearch(searchTerm: string): void {
    if (searchTerm !== this.searchTerm$$.getValue()) {
      this.campaigns$$.next([]);
      this.offset$$.next(0);
    }
    this.loadingCampaigns$$.next(true);
    this.searchTerm$$.next(searchTerm);
  }

  private handleCampaignResponse(campaigns: SimpleCampaign[]): void {
    const previousCampaign = this.campaigns$$.getValue() || [];
    this.campaigns$$.next(previousCampaign.concat(campaigns));
    this.loadingCampaigns$$.next(false);
  }

  private handleRecipientCampaigns(recipientCampaigns: RecipientCampaignsResponse): void {
    this.recipientCampaigns$$.next(recipientCampaigns);
    this.loadingRecipientCampiagns$$.next(false);
  }
}
